import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  AntDesign,
  Ionicons,
  MaterialIcons,
  Octicons,
} from "@expo/vector-icons";
import { v4 as uuidv4 } from "uuid";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import IconEmptyView from "../../Components/IconEmptyView";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { useToast } from "../../contexts/ToastContext";
import useChevronRotation from "../../hooks/useChevronRotation";
import { useDevice, useScreenDimensions } from "../../_layout";
import { EquivalentEditorView } from "./editor/EquivalentEditorView";
import { buildEquivalentDuplicateDraft } from "./equivalentLogic";
import { sortNutritionEquivalents } from "./equivalentSearch";
import { getNutritionEntityDisplayName } from "../shared/nutritionEntityNameUtils";
import {
  formatCalorieValue,
  formatMacroValue,
  getUiMacroShortLabel,
  UI_MACRO_DISPLAY_ORDER,
} from "../shared/nutritionFormatters";
import {
  getSearchEnterTarget,
  isEnterKeyEvent,
} from "../shared/searchEnterTarget";
import { NutritionLibrarySkeletonGrid } from "../shared/NutritionLibrarySkeletonGrid";
import { useDesktopAutoFocusTextInput } from "../shared/useDesktopAutoFocusTextInput";
import { useLatchedEditorPersistedState } from "../shared/useLatchedEditorPersistedState";

const EQUIVALENT_SORT_OPTIONS = [
  {
    key: "default",
    labelKey: "default-string",
    field: null,
  },
  {
    key: "energy",
    labelKey: "energy-string",
    field: "calories",
  },
  ...UI_MACRO_DISPLAY_ORDER.map((field) => ({
    key: field,
    labelKey: `${field}-string`,
    field,
  })),
];

function getEquivalentCardKey(equivalent) {
  return String(equivalent?.id ?? "").trim().toLowerCase() || null;
}

const EquivalentCard = ({
  equivalent,
  language,
  screenSize,
  onPress,
  t,
  isEnterTarget = false,
  onHoverStart,
}) => {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);
  const displayName = getNutritionEntityDisplayName("equivalent", equivalent, language);
  const equivalentOptionNames = (
    Array.isArray(equivalent?.sortedOptions)
      ? equivalent.sortedOptions
      : Array.isArray(equivalent?.options)
        ? equivalent.options
        : []
  )
    .map((option) => String(option?.name ?? "").trim())
    .filter(Boolean);
  const previewFoods = equivalentOptionNames.slice(0, 3);
  const remainingCount = Math.max(
    equivalentOptionNames.length - previewFoods.length,
    0,
  );
  const macroValues = {
    protein: equivalent?.protein,
    carbs: equivalent?.carbs,
    fats: equivalent?.fats,
  };
  const columnCount = screenSize === "small" ? 1 : 2;
  const gap = screenSize === "small" ? 8 : 12;
  const cardWidth = `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`;

  return (
    <View style={{ width: cardWidth }}>
      <TouchableOpacity
        activeOpacity={0.96}
        onPress={onPress}
        onMouseEnter={
          !isMobile
            ? () => {
                setIsHovered(true);
                onHoverStart?.();
              }
            : undefined
        }
        onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
        style={styles.cardTouchable}
      >
        <View
          style={[
            styles.card,
            isHovered && styles.cardHovered,
            isEnterTarget && styles.cardEnterTarget,
          ]}
        >
          <View style={styles.cardLeft}>
            <Text numberOfLines={1} style={styles.cardName}>
              {displayName}
            </Text>
            <View style={styles.previewList}>
              {previewFoods.map((foodName) => (
                <Text key={foodName} numberOfLines={1} style={styles.previewRowName}>
                  {foodName}
                </Text>
              ))}
              {remainingCount > 0 ? (
                <Text numberOfLines={1} style={styles.previewOverflow}>
                  +{remainingCount}{" "}
                  {(remainingCount === 1
                    ? t("food-string")
                    : t("foods-string")
                  ).toLowerCase()}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={styles.cardRight}>
            <Text style={styles.cardKcal}>
              {formatCalorieValue(equivalent?.calories)}
              <Text style={styles.cardKcalUnit}> kcal</Text>
            </Text>
            <View style={styles.cardMacroRow}>
              {UI_MACRO_DISPLAY_ORDER.map((key) => (
                <Text key={key} style={styles.cardMacroValue}>
                  {getUiMacroShortLabel(key, language)}{" "}
                  <Text style={styles.cardMacroNum}>
                    {formatMacroValue(macroValues[key])}g
                  </Text>
                </Text>
              ))}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

function createEmptyEquivalent() {
  return {
    id: uuidv4(),
    name: "",
    options: [],
  };
}

export const EquivalentsView = observer(({ coach }) => {
  const { i18n, t } = useTranslation();
  const { showToast } = useToast();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const { screenSize } = useScreenDimensions();
  const { isMobile, isIOS } = useDevice();
  const [selectedSortKey, setSelectedSortKey] = useState("default");
  const [isSortTriggerHovered, setIsSortTriggerHovered] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [enterTargetOverrideKey, setEnterTargetOverrideKey] = useState(null);
  const [isCreateButtonHovered, setIsCreateButtonHovered] = useState(false);
  const searchInputRef = useRef(null);
  const isSearchInputPressingRef = useRef(false);
  const sortChevronRotation = useChevronRotation(
    isSortMenuOpen,
    "closedDownOpenUp",
  );
  useDesktopAutoFocusTextInput(searchInputRef, { isMobile });
  const sortOptions = useMemo(
    () =>
      EQUIVALENT_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const selectedSortOption =
    sortOptions.find((option) => option.key === selectedSortKey) || sortOptions[0];
  const coachEquivalents = Array.isArray(coach?.nutritionStore?.sortedEquivalents)
    ? coach.nutritionStore.sortedEquivalents
    : [];
  const shouldShowInitialSkeletons =
    coach?.nutritionStore?.isFetchingEquivalents === true &&
    coach?.nutritionStore?.hasFetchedEquivalents !== true;
  const editingEquivalentId =
    String(coach?.nutritionEditingEquivalentId ?? "").trim().toLowerCase() || null;
  const allEquivalents = coachEquivalents;
  const normalizedSearch = searchText.trim().toLowerCase();
  const currentEditorEquivalent = editingEquivalentId
    ? coach?.nutritionStore?.equivalentEntitiesById?.get?.(editingEquivalentId) ?? null
    : null;
  const isEditingExistingEquivalent = useLatchedEditorPersistedState(
    editingEquivalentId,
    currentEditorEquivalent,
  );
  // Keep the browser projection in observer render flow so same-entity draft field
  // updates, such as naming a newly created equivalent before closing the editor,
  // are reflected immediately when returning to the list.
  const filteredEquivalents = sortNutritionEquivalents(
    allEquivalents.filter((equivalent) =>
      getNutritionEntityDisplayName("equivalent", equivalent, language)
        .toLowerCase()
        .includes(normalizedSearch),
    ),
    selectedSortOption?.field,
    language,
  );
  const enterTargetEquivalent = useMemo(
    () =>
      getSearchEnterTarget({
        searchText,
        items: filteredEquivalents,
        getItemKey: getEquivalentCardKey,
        overrideKey: enterTargetOverrideKey,
      }),
    [enterTargetOverrideKey, filteredEquivalents, searchText],
  );
  const hasEnterTarget = enterTargetEquivalent != null;
  const enterTargetEquivalentKey = getEquivalentCardKey(enterTargetEquivalent);
  const focusSearchInput = useCallback(() => {
    if (isSearchInputPressingRef.current) {
      return;
    }

    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (editingEquivalentId && !currentEditorEquivalent) {
      coach?.setProperty?.("nutritionEditingEquivalentId", null);
    }
  }, [coach, currentEditorEquivalent, editingEquivalentId]);

  useEffect(() => {
    setEnterTargetOverrideKey(null);
  }, [searchText]);

  const openCreateEquivalent = useCallback(() => {
    const nextEquivalent = coach?.nutritionStore?.createDraftEquivalent?.(
      createEmptyEquivalent(),
    ) ?? createEmptyEquivalent();
    coach?.setProperty?.("nutritionEditingEquivalentId", nextEquivalent.id);
  }, [coach]);
  const openEditEquivalent = useCallback((equivalent) => {
    coach?.setProperty?.("nutritionEditingEquivalentId", equivalent?.id ?? null);
  }, [coach]);
  const openEnterTargetEquivalent = useCallback(() => {
    if (!enterTargetEquivalent) {
      return;
    }

    openEditEquivalent(enterTargetEquivalent);
  }, [enterTargetEquivalent, openEditEquivalent]);
  const closeEquivalentEditor = useCallback(async () => {
    if (editingEquivalentId) {
      try {
        await coach?.nutritionStore?.finalizeEquivalentEditorClose?.(editingEquivalentId);
      } catch (error) {
        console.error("Failed to finalize equivalent editor close:", error);
        showToast("nutritionEquivalentDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionEquivalentDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        return false;
      }
    }
    coach?.setProperty?.("nutritionEditingEquivalentId", null);
    return true;
  }, [coach, editingEquivalentId, showToast]);
  const handleDuplicateEquivalent = useCallback(() => {
    const equivalentDraft = currentEditorEquivalent?.getDraftSnapshot?.();
    if (!coach?.nutritionStore || !equivalentDraft) {
      return;
    }

    const nextEquivalent = coach.nutritionStore.createDraftEquivalent(
      buildEquivalentDuplicateDraft(equivalentDraft, { language }),
    );
    coach.nutritionStore.ensureEntityHasUnsyncedDraftForImmediateSave(nextEquivalent);
    nextEquivalent.saveNow?.().catch((error) => {
      console.error("Failed to queue duplicated equivalent save:", error);
    });
    coach?.setProperty?.("nutritionEditingEquivalentId", nextEquivalent.id);
  }, [coach, currentEditorEquivalent, language]);
  const handleDeleteEquivalent = useCallback(
    async (equivalentEntity) => {
      const equivalentId = equivalentEntity?.id ?? currentEditorEquivalent?.id;
      if (!coach?.nutritionStore || !equivalentId) {
        return;
      }

      const deleteEquivalentName = getNutritionEntityDisplayName(
        "equivalent",
        equivalentEntity ?? currentEditorEquivalent,
        language,
      );

      try {
        await coach.nutritionStore.deleteEquivalent(equivalentId);
        await closeEquivalentEditor();
        showToast("nutritionEquivalentDeleteSuccessTitle-string", {
          type: "info",
          title: "nutritionEquivalentDeleteSuccessTitle-string",
          description: "nutritionEquivalentDeleteSuccessDescription-string",
          variables: {
            name: deleteEquivalentName,
          },
        });
      } catch (error) {
        console.error("Failed to delete equivalent:", error);
        showToast("nutritionEquivalentDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionEquivalentDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        throw error;
      }
    },
    [closeEquivalentEditor, coach, currentEditorEquivalent, language, showToast],
  );
  const handleSearchKeyPress = useCallback(
    (event) => {
      if (!isEnterKeyEvent(event)) {
        return;
      }

      if (shouldShowInitialSkeletons || !hasEnterTarget) {
        return;
      }

      event?.preventDefault?.();
      openEnterTargetEquivalent();
    },
    [hasEnterTarget, openEnterTargetEquivalent, shouldShowInitialSkeletons],
  );

  if (currentEditorEquivalent) {
    return (
      <EquivalentEditorView
        coach={coach}
        equivalent={currentEditorEquivalent}
        isEditing={isEditingExistingEquivalent}
        onBack={closeEquivalentEditor}
        onDuplicate={handleDuplicateEquivalent}
        onDelete={handleDeleteEquivalent}
      />
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerCard}>
        <View style={styles.searchRow}>
          <TouchableOpacity
            activeOpacity={1}
            onPress={focusSearchInput}
            style={styles.searchField}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={t("searchByName-string")}
            aria-label={t("searchByName-string")}
          >
            <View style={styles.searchIcon}>
              <Octicons name="search" size={14} color={Colors.grayColor} />
            </View>
            <TextInput
              ref={searchInputRef}
              value={searchText}
              onChangeText={setSearchText}
              onKeyPress={handleSearchKeyPress}
              onPressIn={() => {
                isSearchInputPressingRef.current = true;
              }}
              onPressOut={() => {
                isSearchInputPressingRef.current = false;
              }}
              onBlur={() => {
                isSearchInputPressingRef.current = false;
              }}
              placeholder={t("searchByName-string")}
              placeholderTextColor={Colors.grayColor}
              style={[
                styles.searchInput,
                isMobile && isIOS ? styles.searchInputIOS : null,
              ]}
              accessibilityLabel={t("searchByName-string")}
              aria-label={t("searchByName-string")}
            />
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setIsCreateButtonHovered(false);
              openCreateEquivalent();
            }}
            onMouseEnter={!isMobile ? () => setIsCreateButtonHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsCreateButtonHovered(false) : undefined}
          >
            <View
              style={[
                styles.createButton,
                isCreateButtonHovered ? styles.createButtonHovered : null,
              ]}
            >
              <AntDesign name="plus" size={12} color={Colors.whiteColor} />
              <Text style={styles.createButtonText}>{t("create-string")}</Text>
            </View>
          </TouchableOpacity>
        </View>
        <View style={styles.divider} />
        <View style={styles.bottomRow}>
          <FloatingMenu
            placement="bottom-end"
            width={220}
            items={sortOptions}
            open={isSortMenuOpen}
            onOpenChange={setIsSortMenuOpen}
            trigger={
              <View
                style={styles.sortTrigger}
                onMouseEnter={!isMobile ? () => setIsSortTriggerHovered(true) : undefined}
                onMouseLeave={!isMobile ? () => setIsSortTriggerHovered(false) : undefined}
              >
                <MaterialIcons
                  name="sort"
                  size={14}
                  color={Colors.darkgrayColor}
                />
                {selectedSortOption?.field == null ? (
                  <Text
                    style={[
                      styles.sortTriggerLabel,
                      isSortTriggerHovered ? styles.sortTriggerLabelHovered : null,
                    ]}
                  >
                    {t("sortBy-string")}
                  </Text>
                ) : (
                  <Text style={styles.sortTriggerValue}>
                    {selectedSortOption?.label}
                  </Text>
                )}
                <Animated.View
                  style={{ transform: [{ rotate: sortChevronRotation }] }}
                >
                  <Ionicons
                    name="chevron-down"
                    size={14}
                    color={
                      isSortTriggerHovered ? Colors.darkgrayColor : Colors.grayColor
                    }
                  />
                </Animated.View>
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.sortMenuItem}
                hoverStyle={styles.sortMenuItemHover}
                onPress={() => {
                  setSelectedSortKey(item.key);
                  close();
                }}
              >
                <Text
                  style={[
                    styles.sortMenuItemText,
                    item.key === selectedSortKey
                      ? styles.sortMenuItemTextActive
                      : null,
                  ]}
                >
                  {item.label}
                </Text>
                {item.key === selectedSortKey ? (
                  <Ionicons
                    name="checkmark"
                    size={15}
                    color={Colors.bubbleColor}
                  />
                ) : null}
              </HoverableTouchableOpacity>
            )}
          />
        </View>
      </View>

      <View style={styles.results}>
        {shouldShowInitialSkeletons ? (
          <NutritionLibrarySkeletonGrid
            screenSize={screenSize}
            variant="equivalent"
          />
        ) : allEquivalents.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="git-compare-outline"
            mainText="equivalentLibraryEmptyTitle-string"
            subText="equivalentLibraryEmptyDescription-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : filteredEquivalents.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="search-outline"
            mainText="noEquivalentsFound-string"
            subText="adjustFiltersOrSearch-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : (
          <View
            style={[
              styles.cardGrid,
              { gap: screenSize === "small" ? 8 : 12 },
            ]}
          >
            {filteredEquivalents.map((equivalent) => (
              <EquivalentCard
                key={equivalent?.id}
                equivalent={equivalent}
                language={language}
                screenSize={screenSize}
                t={t}
                isEnterTarget={
                  hasEnterTarget &&
                  getEquivalentCardKey(equivalent) === enterTargetEquivalentKey
                }
                onHoverStart={() =>
                  setEnterTargetOverrideKey(getEquivalentCardKey(equivalent))
                }
                onPress={() => openEditEquivalent(equivalent)}
              />
            ))}
          </View>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  headerCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    minHeight: 50,
    paddingVertical: 10,
    gap: 10,
  },
  searchField: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    cursor: "text",
  },
  searchIcon: {
    justifyContent: "center",
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    marginLeft: 10,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  searchInputIOS: {
    fontSize: 16,
  },
  divider: {
    height: 0.5,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 14,
  },
  bottomRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 12,
  },
  createButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.bubbleColor,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    flexShrink: 0,
    transition: "opacity 180ms ease-in-out",
  },
  createButtonHovered: {
    opacity: 0.6,
  },
  createButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
    marginLeft: 6,
  },
  sortTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    flexShrink: 0,
  },
  sortTriggerLabel: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  sortTriggerLabelHovered: {
    color: Colors.darkgrayColor,
  },
  sortTriggerValue: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  sortMenuItem: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  sortMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  sortMenuItemText: {
    fontSize: 13,
    fontWeight: "400",
    color: Colors.darkgrayColor,
  },
  sortMenuItemTextActive: {
    color: Colors.bubbleColor,
    fontWeight: "500",
  },
  results: {
    marginTop: 24,
    marginBottom: 36,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  cardTouchable: {
    borderRadius: 12,
    overflow: "hidden",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
  },
  card: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    paddingVertical: 16,
    paddingHorizontal: 18,
    overflow: "hidden",
    transitionProperty: "border-color, background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  cardHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: "#FDFDFD",
  },
  cardEnterTarget: {
    borderColor: Colors.darkgrayColor,
    backgroundColor: "#FDFDFD",
  },
  cardLeft: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  cardName: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
    color: Colors.blackColor,
  },
  previewList: {
    marginTop: 6,
    gap: 2,
    height: 70,
    justifyContent: "flex-start",
  },
  previewRowName: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.darkgrayColor,
  },
  previewOverflow: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.grayColor,
    fontStyle: "italic",
    marginTop: 2,
  },
  cardRight: {
    width: 120,
    alignItems: "flex-end",
  },
  cardReferenceLabel: {
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginBottom: 4,
  },
  cardKcal: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  cardKcalUnit: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  cardMacroRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginTop: 4,
  },
  cardMacroValue: {
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    color: Colors.grayColor,
  },
  cardMacroNum: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
});
